/**
 * The NAB lockup, for the top of the side rail.
 *
 * ## Which file this is, and why it is a raster
 *
 * The brand pack ships the lockup three ways and only one of them is both complete and correct:
 *
 *   * `logo_nab.svg` (321 KB) — an SVG wrapping a base64 PNG. Not a vector at all, but the only
 *     asset carrying the mark's real colours: blue wordmark, grey ellipse, orange swoosh.
 *   * `nab-logo.svg` (38 KB) — a genuine vector of the same lockup, but flattened to a single blue.
 *     Crisp and scalable; missing two of the three brand colours.
 *   * `nab-symbol.svg` (2.8 KB) — the mark without the wordmark, also single-blue. Clean.
 *
 * Both were rendered and compared before choosing. The full-colour lockup is what the design uses
 * and what the bank will recognise, so it wins — but shipping a 321 KB SVG to display a 210×40 image
 * is not the way to use it. The PNG was extracted from inside the wrapper and resampled to 440px
 * wide, which is 2× the largest size it is ever drawn at: 51 KB instead of 321 KB, for the same
 * pixels on screen.
 *
 * `nab-symbol.svg` is kept for the places a single colour is right anyway — the assistant's avatar
 * beside its replies, where the mark sits at 32px and crispness matters more than the swoosh.
 *
 * If NAB later supplies a full-colour vector, replace `nab-logo.png` and delete this paragraph.
 */

import logoUrl from "../assets/nab-logo.png";
import symbolUrl from "../assets/nab-symbol.svg";

export interface BrandMarkProps {
  /**
   * Accessible name. Required rather than defaulted: this is the bank's name, it differs by
   * language, and a default here would be an untranslated English string on the Arabic interface.
   */
  label: string;
  className?: string;
}

/** The full lockup — wordmark and symbol. For the rail header. */
export function BrandMark({ label, className }: BrandMarkProps): JSX.Element {
  return (
    <img
      src={logoUrl}
      alt={label}
      // Capped on both axes. Height alone lets a wide render push the rail's padding out; width
      // alone lets a tall one grow the header past the rule it has to line up with.
      className={className ?? "h-10 w-auto max-w-[210px] object-contain"}
    />
  );
}

/** The symbol alone. For the assistant's avatar, and anywhere too small for the wordmark. */
export function BrandSymbol({ label, className }: BrandMarkProps): JSX.Element {
  return (
    <img src={symbolUrl} alt={label} className={className ?? "h-8 w-8 object-contain"} />
  );
}
